/**
 * CountdownCell — Phase 13 / DISP-06 + DISP-08.
 *
 * Relative Countdown-Zelle fuer die Column "Link gueltig bis" in OpenPaymentsList.
 * Zeigt je nach Restlaufzeit unterschiedliche Styles:
 *   - < 0: roter Tag + CloseCircleOutlined, Text "abgelaufen"
 *   - < 48h: gold Tag + WarningOutlined, Text "in Xh Ym"
 *   - >= 48h: plain span, Text "in Xd Yh"
 *
 * Tooltip zeigt immer das absolute Datum ("Gueltig bis DD.MM.YY HH:mm").
 *
 * Refresh-Intervall: Standard 60s (via setInterval). Ueber `refreshIntervalMs`-Prop
 * in Tests ueberschreibbar.
 *
 * Konvention:
 *   - React 17 + Ant Design 4.24 kompatibel (kein v5-API, kein useId).
 *   - i18n-Keys via vollem Pfad (Proxy-Regel aus MEMORY.md).
 *   - Kein moment().fromNow() — eigene Formatierung (Pitfall 8: fromNow rundet zu grob).
 */

import React, {FC, useEffect, useState} from "react";
import {Tag, Tooltip} from "antd";
import {WarningOutlined, CloseCircleOutlined} from "@ant-design/icons";
import moment from "moment";
import i18n from "../../services/i18n";

export interface CountdownCellProps {
    expiresAt: Date | string | null | undefined;
    /** Refresh-Intervall in ms. Default 60000 (60s). In Tests ueberschreibbar. */
    refreshIntervalMs?: number;
}

const CountdownCell: FC<CountdownCellProps> = ({expiresAt, refreshIntervalMs = 60000}) => {
    // `forceRefresh` re-rendert die Komponente alle 60s ohne die Prop zu aendern,
    // damit die relative Zeit aktuell bleibt.
    const [, forceRefresh] = useState(0);

    useEffect(() => {
        if (!expiresAt) {
            return;
        }
        const interval = setInterval(() => {
            forceRefresh((n) => n + 1);
        }, refreshIntervalMs);
        return () => {
            clearInterval(interval);
        };
    }, [expiresAt, refreshIntervalMs]);

    if (!expiresAt) {
        return <span>{"-"}</span>;
    }

    const expiryMoment = moment(expiresAt);
    const now = moment();
    const diffMin = expiryMoment.diff(now, "minutes");

    // Tooltip: immer absolut (Umlaute aus i18n-File)
    const tooltipText = i18n.containers.payments.PaymentsListColumns.countdownTooltipAbsolute({
        expiresAt: expiryMoment.format("DD.MM.YY HH:mm"),
    });

    // Abgelaufen
    if (diffMin < 0) {
        return (
            <Tooltip title={tooltipText}>
                <Tag color="red" icon={<CloseCircleOutlined />} style={{margin: 0}}>
                    {i18n.containers.payments.PaymentsListColumns.countdownExpired()}
                </Tag>
            </Tooltip>
        );
    }

    // Label-Berechnung — Pitfall 8: eigene Formatierung statt moment.fromNow().
    let label: string;
    if (diffMin >= 1440) {
        const days = Math.floor(diffMin / 1440);
        const hours = Math.floor((diffMin % 1440) / 60);
        label = i18n.containers.payments.PaymentsListColumns.countdownDaysHours({
            days: String(days),
            hours: String(hours),
        });
    } else if (diffMin >= 60) {
        const hours = Math.floor(diffMin / 60);
        const minutes = diffMin % 60;
        label = i18n.containers.payments.PaymentsListColumns.countdownHoursMinutes({
            hours: String(hours),
            minutes: String(minutes),
        });
    } else {
        label = i18n.containers.payments.PaymentsListColumns.countdownMinutes({
            minutes: String(diffMin),
        });
    }

    // Warn-Schwelle: < 48h Restlaufzeit
    const hoursRemaining = expiryMoment.diff(now, "hours");
    if (hoursRemaining < 48) {
        return (
            <Tooltip title={tooltipText}>
                <Tag color="gold" icon={<WarningOutlined />} style={{margin: 0}}>
                    {label}
                </Tag>
            </Tooltip>
        );
    }

    // Default >= 48h: plain span
    return (
        <Tooltip title={tooltipText}>
            <span>{label}</span>
        </Tooltip>
    );
};

export default CountdownCell;
